import { useEffect, useRef, useState } from "react";
import { useContent } from "../../content/ContentContext";
import { getPlan, refinePlan } from "../../api/planner";
import { Button } from "../../components/Button";
import { ErrorSummary } from "../../components/ErrorSummary";
import { ExplainerDisclosure } from "../../components/ExplainerDisclosure";
import { LoadingState } from "../../components/LoadingState";
import { fieldOptionsIfValid } from "../../api/estimate";
import type { EstimatePreview, FieldOption, MissingInput } from "../../types";
import { RefinementChangeSummary } from "./RefinementChangeSummary";
import { useRefinementFlow, type RefinementSubmit } from "./useRefinementFlow";

type FieldValue = string | boolean;
type FieldValues = Record<string, FieldValue>;

function decimal(value: string, allowNegative = false): string | null {
  const compact = value.trim().replace(/\s/g, "");
  const normalized = compact.includes(",")
    ? compact.replace(/\./g, "").replace(",", ".")
    : compact;
  const pattern = allowNegative ? /^-?\d+(\.\d{1,2})?$/ : /^\d+(\.\d{1,2})?$/;
  if (!pattern.test(normalized)) return null;
  const [whole, fraction = ""] = normalized.split(".");
  return `${whole}.${fraction.padEnd(2, "0")}`;
}

function integer(value: FieldValue | undefined): number | null {
  const raw = String(value ?? "");
  return /^\d+$/.test(raw) ? Number.parseInt(raw, 10) : null;
}

function count(values: FieldValues, key: string, minimum = 1): number {
  return Math.max(minimum, integer(values[key]) ?? minimum);
}

interface FieldProps {
  name: string;
  label: string;
  values: FieldValues;
  change: (key: string, value: FieldValue) => void;
  error?: string;
  explainerKey?: string;
}

function MoneyField({ name, label, values, change, error, explainerKey }: FieldProps) {
  return <><label>{label}<input id={name} name={name} aria-invalid={Boolean(error)} aria-describedby={error ? `${name}-error` : undefined} inputMode="decimal" value={String(values[name] ?? "")} onChange={(event) => change(name, event.target.value)} /></label>{error && <span id={`${name}-error`} className="field-error">{error}</span>}{explainerKey && <ExplainerDisclosure explainerKey={explainerKey} className="explainer--option" />}</>;
}

function IntegerField({ name, label, values, change, error, explainerKey }: FieldProps) {
  return <><label>{label}<input id={name} name={name} aria-invalid={Boolean(error)} aria-describedby={error ? `${name}-error` : undefined} inputMode="numeric" pattern="[0-9]*" value={String(values[name] ?? "")} onChange={(event) => change(name, event.target.value)} /></label>{error && <span id={`${name}-error`} className="field-error">{error}</span>}{explainerKey && <ExplainerDisclosure explainerKey={explainerKey} className="explainer--option" />}</>;
}

function SelectField({ name, label, options, values, change, error, explainerKey }: FieldProps & { options: FieldOption[] }) {
  return <><label>{label}<select id={name} name={name} aria-invalid={Boolean(error)} aria-describedby={error ? `${name}-error` : undefined} value={String(values[name] ?? "")} onChange={(event) => change(name, event.target.value)}><option value="">Choose</option>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>{error && <span id={`${name}-error`} className="field-error">{error}</span>}{explainerKey && <ExplainerDisclosure explainerKey={explainerKey} className="explainer--option" />}</>;
}

function CheckboxField({ name, label, values, change, explainerKey }: FieldProps) {
  return <><label><input name={name} type="checkbox" checked={Boolean(values[name])} onChange={(event) => change(name, event.target.checked)} /> {label}</label>{explainerKey && <ExplainerDisclosure explainerKey={explainerKey} className="explainer--option" />}</>;
}

function Repeater({ legend, countKey, minimum = 1, fixedCount, values, change, children }: { legend: string; countKey: string; minimum?: number; fixedCount?: number; values: FieldValues; change: FieldProps["change"]; children: (index: number) => React.ReactNode }) {
  const rows = fixedCount ?? count(values, countKey, minimum);
  return <fieldset><legend>{legend}</legend>
    {Array.from({ length: rows }, (_, index) => <fieldset key={index}><legend>{legend.replace(/s$/, "")} {index + 1}</legend>{children(index)}</fieldset>)}
    {fixedCount === undefined && <div className="action-row"><Button type="button" variant="quiet" onClick={() => change(countKey, String(rows + 1))}>Add another</Button>{rows > minimum && <Button type="button" variant="quiet" onClick={() => change(countKey, String(rows - 1))}>Remove last</Button>}</div>}
  </fieldset>;
}

type StructuredField = MissingInput["fields"][number];
type ControlKind = "money" | "integer" | "select" | "check";

/** One input of a structured question. The same description drives rendering and validation. */
interface Control {
  kind: ControlKind;
  /** The DOM id and form key; unique within the page, repeater rows suffixed by their index. */
  id: string;
  /** The server field this control collects: where its label and, for a select, its options come from. */
  field: string;
  label: string;
  optional?: boolean;
  negative?: boolean;
  explainer?: string;
}

interface RepeaterLayout {
  legend: string;
  countKey: string;
  minimum: number;
  fixedCount?: number;
  rows: (index: number) => Control[];
}

type LayoutItem = Control | RepeaterLayout;

const isRepeater = (item: LayoutItem): item is RepeaterLayout => "rows" in item;

/**
 * W6-02: what each structured question asks, as data. `null` means the question is not
 * one this version can collect safely. Control ids, labels, fallbacks and explainers are
 * exactly those the form used before this story; only the choices of a select changed
 * (they now come from the server — see `fieldOptionsIfValid`).
 */
function layoutFor(code: string, fields: StructuredField[]): LayoutItem[] | null {
  const find = (fieldCode: string) => fields.find((field) => field.code === fieldCode);
  const label = (fieldCode: string, fallback: string) => find(fieldCode)?.label ?? fallback;
  const shown = (fieldCode: string) => find(fieldCode)?.required !== false;
  const declared = (fieldCode: string) => find(fieldCode) !== undefined;
  // W4-01: a structured field's own explainer, when the server bound one — see
  // planner/catalogue/inputs.py's structured_fields entries. Absent for most fields.
  const explain = (fieldCode: string) => find(fieldCode)?.explainer_keys?.[0];
  const make = (kind: ControlKind) => (id: string, field: string, fallback: string, extra: Partial<Control> = {}): Control => ({ kind, id, field, label: label(field, fallback), ...extra });
  const money = make("money");
  const whole = make("integer");
  const select = make("select");
  const check = make("check");

  if (code === "benefit_assets_details") {
    const residentField = find("co_residents_eur");
    return [
      money("applicant_eur", "applicant_eur", "Your assets in euros"),
      ...(shown("partner_eur") ? [money("partner_eur", "partner_eur", "Your benefit partner’s assets in euros", { optional: !find("partner_eur")?.required })] : []),
      ...(shown("co_residents_eur") ? [{ legend: label("co_residents_eur", "Co-resident assets"), countKey: "asset_count", minimum: 0, fixedCount: residentField?.count, rows: (index: number) => [money(`asset_${index}`, "co_residents_eur", "Assets in euros")] }] : []),
    ];
  }
  if (code === "business_annual_profit") return [
    money("profit", "fiscal_business_profit_eur", "Fiscal business profit", { label: "Fiscal business profit", negative: true }),
    money("other", "other_zvw_contribution_income_eur", "Other Zvw contribution income", { label: "Other Zvw contribution income", explainer: explain("other_zvw_contribution_income_eur") }),
    check("unknown", "has_other_zvw_income_unknown", "Some other Zvw income is unknown", { label: "Some other Zvw income is unknown" }),
  ];
  if (code === "children_benefit_details") return [{ legend: label("children", "Children"), countKey: "child_count", minimum: 1, rows: (index: number) => [
    whole(`child_age_${index}`, "age_years", "Age"),
    select(`child_region_${index}`, "residence_region", "Residence region"),
    select(`child_benefit_${index}`, "child_benefit_in_applicant_name", "Child-benefit condition", { explainer: explain("child_benefit_in_applicant_name") }),
  ] }];
  if (code === "institution_and_programme") return [
    select("sector", "education_sector", "Education sector"),
    select("funded", "funded_programme", "Funded programme", { explainer: explain("funded_programme") }),
    select("mode", "study_mode", "Study mode"),
    select("degree", "degree_level", "Degree level"),
    select("prior", "prior_same_level_funded_nl_degree", "Prior same-level funded Dutch degree"),
    select("exception", "first_education_or_health_exception", "First education or health exception"),
    select("nationality", "statutory_nationality_condition", "Statutory nationality condition", { explainer: explain("statutory_nationality_condition") }),
  ];
  if (code === "vehicle_characteristics") return [
    select("class", "vehicle_class", "Vehicle class"),
    select("condition", "new_or_used", "New or used"),
    whole("co2", "co2_g_per_km", "Official CO₂ g/km", { explainer: explain("co2_g_per_km") }),
    // An optional choice the server did not declare has no server-owned options to offer, so it
    // is left out rather than offered from a client-side list (W6-02).
    ...(shown("fuel_type") && declared("fuel_type") ? [select("fuel", "fuel_type", "Fuel type", { optional: true })] : []),
    ...(shown("mass_rijklaar_kg") ? [whole("mass", "mass_rijklaar_kg", "Mass ready to drive in kg", { optional: true })] : []),
    ...(shown("province") && declared("province") ? [select("province", "province", "Province", { optional: true })] : []),
  ];
  if (code === "childcare_hours_and_rate") return [
    select("registered", "registered_childcare_confirmed", "Registered childcare confirmed"),
    select("eligible", "eligible_work_or_study_condition_confirmed", "Eligible work or study condition"),
    money("income", "joint_toetsingsinkomen", "Joint household income", { explainer: explain("joint_toetsingsinkomen") }),
    whole("working_months", "eligible_working_months_2026", "Eligible working months in 2026"),
    { legend: "Children", countKey: "care_count", minimum: 1, rows: (index: number) => [
      select(`care_type_${index}`, "care_type", "Care type"),
      money(`hours_${index}`, "contracted_hours_per_month", "Contracted hours per month"),
      money(`rate_${index}`, "hourly_rate_eur", "Hourly rate"),
      whole(`care_months_${index}`, "care_months_2026", "Care months in 2026"),
    ] },
  ];
  if (code === "housing_benefit_household_details") return [
    whole("household_count", "household_member_count", "Household member count"),
    check("all_under_21", "all_household_members_under_21", "All household members are under 21"),
    check("exception", "under_21_child_or_disability_exception", "Child or disability exception applies"),
    check("subtenant", "has_subtenant_special_case", "A subtenant special case applies"),
    check("other_special", "has_other_special_case", "Another special case applies"),
    { legend: label("co_residents", "Co-residents"), countKey: "resident_count", minimum: 0, rows: (index: number) => [
      whole(`resident_age_${index}`, "age_at_start_of_2026", "Age at start of 2026"),
      money(`resident_income_${index}`, "toetsingsinkomen_eur", "Household income in euros", { explainer: explain("toetsingsinkomen_eur") }),
      check(`resident_under_23_${index}`, "is_child_or_equivalent_under_23", "Child or equivalent under 23"),
    ] },
  ];
  return null;
}

/** Every select of the question needs valid server options, or the whole question is unsafe to show. */
function choicesFor(layout: LayoutItem[], fields: StructuredField[]): Map<string, FieldOption[]> | null {
  const choices = new Map<string, FieldOption[]>();
  for (const control of layout.flatMap((item) => (isRepeater(item) ? item.rows(0) : [item]))) {
    if (control.kind !== "select") continue;
    const field = fields.find((candidate) => candidate.code === control.field);
    const options = field ? fieldOptionsIfValid(field) : null;
    if (!options) return null;
    choices.set(control.field, options);
  }
  return choices;
}

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * W6-02 (F5): which controls are invalid, by their own id, so an error can name and link the
 * exact field. A repeated row is told apart by the row's name already on screen ("Child 2").
 */
function invalidControls(layout: LayoutItem[], values: FieldValues): { id: string; label: string }[] {
  const invalid: { id: string; label: string }[] = [];
  const check = (control: Control, qualifier: string) => {
    if (control.kind === "check") return;
    const raw = String(values[control.id] ?? "");
    if (control.optional && raw === "") return;
    const valid = control.kind === "money" ? decimal(raw, control.negative) !== null
      : control.kind === "integer" ? integer(values[control.id]) !== null
        : raw !== "";
    if (!valid) invalid.push({ id: control.id, label: `${capitalise(control.label)}${qualifier}` });
  };
  for (const item of layout) {
    if (!isRepeater(item)) { check(item, ""); continue; }
    const rows = item.fixedCount ?? count(values, item.countKey, item.minimum);
    const row = item.legend.replace(/s$/, "");
    for (let index = 0; index < rows; index += 1) for (const control of item.rows(index)) check(control, ` (${row} ${index + 1})`);
  }
  return invalid;
}

function StructuredFields({ code, values, change, fields, errors }: { code: string; values: FieldValues; change: FieldProps["change"]; fields: MissingInput["fields"]; errors: Record<string, string> }) {
  const layout = layoutFor(code, fields);
  const choices = layout ? choicesFor(layout, fields) : null;
  if (!layout || !choices) return <p>This input cannot be collected safely in this version.</p>;
  const props = { values, change };
  const control = (item: Control) => {
    const common = { ...props, name: item.id, label: item.label, error: errors[item.id], explainerKey: item.explainer };
    if (item.kind === "money") return <MoneyField key={item.id} {...common} />;
    if (item.kind === "integer") return <IntegerField key={item.id} {...common} />;
    if (item.kind === "select") return <SelectField key={item.id} {...common} options={choices.get(item.field) ?? []} />;
    return <CheckboxField key={item.id} {...common} />;
  };
  return <>{layout.map((item) => isRepeater(item)
    ? <Repeater key={item.countKey} legend={item.legend} countKey={item.countKey} minimum={item.minimum} fixedCount={item.fixedCount} {...props}>{(index) => <>{item.rows(index).map(control)}</>}</Repeater>
    : control(item))}</>;
}

function structured(code: string, value: FieldValues, fields: MissingInput["fields"]): unknown {
  const money = (key: string, allowNegative = false) => decimal(String(value[key] ?? ""), allowNegative);
  if (code === "benefit_assets_details") { const residentCount = fields.find((field) => field.code === "co_residents_eur")?.count ?? 0; return { applicant_eur: money("applicant_eur"), ...(money("partner_eur") === null ? {} : { partner_eur: money("partner_eur") }), co_residents_eur: Array.from({ length: residentCount }, (_, index) => money(`asset_${index}`)) }; }
  if (code === "business_annual_profit") return { fiscal_business_profit_eur: money("profit", true), other_zvw_contribution_income_eur: money("other"), has_other_zvw_income_unknown: Boolean(value.unknown) };
  if (code === "children_benefit_details") return { children: Array.from({ length: count(value, "child_count") }, (_, index) => ({ age_years: integer(value[`child_age_${index}`]), residence_region: value[`child_region_${index}`], child_benefit_in_applicant_name: value[`child_benefit_${index}`] })) };
  if (code === "institution_and_programme") return { education_sector: value.sector, funded_programme: value.funded, study_mode: value.mode, degree_level: value.degree, prior_same_level_funded_nl_degree: value.prior, first_education_or_health_exception: value.exception, statutory_nationality_condition: value.nationality };
  if (code === "vehicle_characteristics") return { vehicle_class: value.class, new_or_used: value.condition, co2_g_per_km: integer(value.co2), ...(value.fuel ? { fuel_type: value.fuel } : {}), ...(value.mass ? { mass_rijklaar_kg: integer(value.mass) } : {}), ...(value.province ? { province: value.province } : {}) };
  if (code === "childcare_hours_and_rate") return { registered_childcare_confirmed: value.registered, eligible_work_or_study_condition_confirmed: value.eligible, joint_toetsingsinkomen: money("income"), eligible_working_months_2026: integer(value.working_months), children: Array.from({ length: count(value, "care_count") }, (_, index) => ({ care_type: value[`care_type_${index}`], contracted_hours_per_month: money(`hours_${index}`), hourly_rate_eur: money(`rate_${index}`), care_months_2026: integer(value[`care_months_${index}`]) })) };
  if (code === "housing_benefit_household_details") return { household_member_count: integer(value.household_count), all_household_members_under_21: Boolean(value.all_under_21), under_21_child_or_disability_exception: Boolean(value.exception), co_residents: Array.from({ length: count(value, "resident_count", 0) }, (_, index) => ({ age_at_start_of_2026: integer(value[`resident_age_${index}`]), toetsingsinkomen_eur: money(`resident_income_${index}`), is_child_or_equivalent_under_23: Boolean(value[`resident_under_23_${index}`]) })), has_subtenant_special_case: Boolean(value.subtenant), has_other_special_case: Boolean(value.other_special) };
  return null;
}

function hasInvalid(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.some(hasInvalid);
  if (typeof value === "object") return Object.values(value as Record<string, unknown>).some(hasInvalid);
  return false;
}

interface RefinementPageProps {
  estimate: EstimatePreview;
  requestedCode?: string;
  online?: boolean;
  announce: (message: string) => void;
  onBack: () => void;
  /** Mirrors the flow's latest snapshot out to the host, on every change — not just at exit — so the overview a user returns to is never stale. */
  onEstimateChange?: (estimate: EstimatePreview) => void;
  submit: RefinementSubmit;
}

/**
 * W4-03: the guided, continuous `Improve my estimates` experience.
 * Presentational — every stateful decision (which question, what changed,
 * skip bookkeeping, idempotency) lives in `useRefinementFlow`. Shared
 * unchanged between both hosts (the anonymous preview and the saved plan)
 * via the `submit` function each one binds. See
 * docs/architecture/features/W4-03.md §2.
 */
export function RefinementPage({
  estimate,
  requestedCode,
  online = true,
  announce,
  onBack,
  onEstimateChange,
  submit,
}: RefinementPageProps) {
  const flow = useRefinementFlow({ initialEstimate: estimate, requestedCode, submit, announce });
  const { current, queue, remaining, answeredCount, canSkip, blockedBy, lastResult, totalChangedItems, skippedRemaining, busy, error, submitAnswer, skip } = flow;
  const { explainer } = useContent();

  useEffect(() => {
    onEstimateChange?.(flow.estimate);
    // `onEstimateChange` intentionally excluded: hosts are expected to pass
    // a stable setter (e.g. `setPreview`), and including it here would risk
    // an extra sync on every host re-render rather than only on genuine
    // snapshot changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.estimate]);

  const [raw, setRaw] = useState("");
  const [values, setValues] = useState<FieldValues>({});
  const [localError, setLocalError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [summaryErrors, setSummaryErrors] = useState<Record<string, string>>({});
  const [resetForCode, setResetForCode] = useState(current?.code);
  const questionHeading = useRef<HTMLHeadingElement>(null);
  const completionHeading = useRef<HTMLHeadingElement>(null);

  // A fresh question must never inherit a stale value. This reset happens
  // here, during render, rather than inside a useEffect: an effect that
  // calls setState synchronously on every dependency change causes the
  // cascading-render pattern React's own guidance (and the
  // react-hooks/set-state-in-effect rule) warns against — see
  // https://react.dev/learn/you-might-not-need-an-effect. Comparing
  // against the code last rendered and adjusting state directly in the
  // render body is React's documented alternative: React discards this
  // render and re-renders immediately with the reset values before
  // anything commits to the screen, with no extra flicker or frame.
  if (current?.code !== resetForCode) {
    setResetForCode(current?.code);
    setRaw("");
    setValues({});
    setFieldErrors({});
    setSummaryErrors({});
    setLocalError("");
  }

  useEffect(() => {
    // Focus movement alone remains an effect — it synchronizes the DOM
    // (an external system) with "the question changed" and updates no
    // React state itself, which is exactly what useEffect is for. See
    // docs/design/features/W4-03.md "Responsive and accessibility".
    questionHeading.current?.focus();
  }, [current?.code]);

  useEffect(() => {
    if (!current) completionHeading.current?.focus();
  }, [current]);

  if (!current) {
    return (
      <section className="empty-state page--narrow" aria-labelledby="refinement-done-title">
        <h1 id="refinement-done-title" tabIndex={-1} ref={completionHeading}>
          {answeredCount > 0
            ? "You’ve answered everything we can ask right now"
            : "Your estimates are already up to date"}
        </h1>
        {answeredCount > 0 && (
          <p>
            You answered {answeredCount} {answeredCount === 1 ? "question" : "questions"}.{" "}
            {totalChangedItems === 0
              ? "Nothing changed for your situation."
              : totalChangedItems === 1
                ? "One estimate changed."
                : `${totalChangedItems} estimates changed.`}
          </p>
        )}
        {skippedRemaining > 0 && (
          <p>
            {skippedRemaining === 1
              ? "One question is still waiting — you can come back to it from your plan."
              : `${skippedRemaining} questions are still waiting — you can come back to them from your plan.`}
          </p>
        )}
        <Button onClick={onBack}>Back to your overview</Button>
      </section>
    );
  }

  const stemExplainer = explainer(current.explainer_keys?.[0]);
  // The number of questions "we can ask right now" is recomputed after every
  // answer and is not a cumulative total — combining it with a running
  // position (as an earlier version of this page did, via
  // `remaining + answeredCount`) produces a fabricated "N of N" whenever
  // exactly one blocker is ever visible at a time, which reads as "this is
  // your last question" every single time, right or wrong. The two facts
  // below are each independently true at every moment and are stated
  // separately instead of forced into one misleading fraction. See
  // docs/architecture/features/W4-03.md §15.
  const remainingLabel = remaining === 1
    ? "One more question we can ask right now"
    : `${remaining} questions we can ask right now`;
  const positionLine = answeredCount === 0
    ? `${remainingLabel}.`
    : `You’ve answered ${answeredCount} question${answeredCount === 1 ? "" : "s"} so far. ${remainingLabel}.`;

  const submitCurrent = () => {
    const required = "Complete this field with a valid value.";
    if (current.value_type === "structured") {
      // W6-02 (F5): name and link every invalid control by its own id.
      const layout = layoutFor(current.code, current.fields);
      const invalid = layout ? invalidControls(layout, values) : [];
      if (invalid.length > 0) {
        setFieldErrors(Object.fromEntries(invalid.map(({ id }) => [id, required])));
        setSummaryErrors(Object.fromEntries(invalid.map(({ id, label }) => [id, `${label}: complete this field with a valid value.`])));
        setLocalError("");
        return;
      }
    }
    let value: unknown = raw;
    if (current.value_type === "decimal") value = decimal(raw);
    if (current.value_type === "integer") value = integer(raw);
    if (current.value_type === "structured") value = structured(current.code, values, current.fields);
    const conditionalMissing = current.code === "benefit_assets_details"
      && current.fields.some((field) => field.code === "partner_eur" && field.required && (value as Record<string, unknown>)[field.code] === undefined);
    if (hasInvalid(value) || conditionalMissing) {
      // A single-input question keeps its one field error. A structured value the control-level
      // check could not explain gets only the generic line: there is no control to point at.
      const single = current.value_type === "structured" ? {} : { [current.code]: required };
      setFieldErrors(single);
      setSummaryErrors(single);
      setLocalError("Complete every required field with a valid value.");
      return;
    }
    setFieldErrors({});
    setSummaryErrors({});
    setLocalError("");
    void submitAnswer(value);
  };

  return (
    <section className="page--narrow" aria-labelledby="refinement-title">
      <p className="eyebrow">You’re improving your estimates</p>
      <h1 id="refinement-title">Improve your estimates</h1>
      <p className="refinement-position">{positionLine}</p>
      <ErrorSummary errors={{ ...summaryErrors, ...((error?.message || localError) ? { answer: error?.message || localError } : {}) }} />
      {answeredCount > 0 && (
        <RefinementChangeSummary result={lastResult ?? null} items={flow.estimate.items} queue={queue} />
      )}
      <form onSubmit={(event) => { event.preventDefault(); submitCurrent(); }} noValidate>
        <div className="question-card" id="answer">
          <h2 id="refinement-question-heading" tabIndex={-1} ref={questionHeading}>{current.label}</h2>
          {current.explainer_keys?.[0] && <ExplainerDisclosure explainerKey={current.explainer_keys[0]} className="explainer--stem" />}
          {(blockedBy.length > 0 || stemExplainer?.where_to_find) && (
            <div className="refinement-purpose">
              <p className="refinement-purpose__eyebrow">Why we’re asking</p>
              {blockedBy.length > 0 && (
                <p>Answering this may improve: {blockedBy.map((item) => item.english_name).join(", ")}.</p>
              )}
              {stemExplainer?.where_to_find && <p>Where to find it: {stemExplainer.where_to_find}</p>}
            </div>
          )}
          {current.value_type === "structured" ? <StructuredFields code={current.code} fields={current.fields} errors={fieldErrors} values={values} change={(key, value) => setValues((existing) => ({ ...existing, [key]: value }))} /> : current.code === "benefit_partner_status" ? <label>{current.label}<select id={current.code} value={raw} onChange={(event) => setRaw(event.target.value)}><option value="">Choose</option><option value="no">No</option><option value="yes">Yes</option><option value="unsure">I’m not sure</option></select></label> : <label>{current.label}<input id={current.code} inputMode={current.value_type === "decimal" ? "decimal" : "numeric"} value={raw} onChange={(event) => setRaw(event.target.value)} /></label>}
        </div>
        <div className="action-row">
          <Button type="button" variant="secondary" onClick={onBack}>Back to your overview</Button>
          {canSkip && <Button type="button" variant="quiet" onClick={skip} aria-describedby="refinement-skip-hint">Skip this question</Button>}
          <Button type="submit" busy={busy} disabled={busy || !online}>Update my estimates</Button>
        </div>
        {canSkip && (
          <p className="field-hint" id="refinement-skip-hint">
            We won’t record an answer. You can come back to it from your plan.
          </p>
        )}
      </form>
    </section>
  );
}

interface SavedRefinementPageProps { publicId: string; requestedCode?: string; online: boolean; navigate: (path: string) => void; announce: (message: string) => void }

export function SavedRefinementPage({ publicId, requestedCode, online, navigate, announce }: SavedRefinementPageProps) {
  const [estimate, setEstimate] = useState<EstimatePreview | null>(null);
  const [loadError, setLoadError] = useState("");
  useEffect(() => { getPlan(publicId).then(setEstimate).catch(() => setLoadError("We couldn’t load your saved plan.")); }, [publicId]);
  if (!estimate) return loadError ? <section className="page--narrow"><ErrorSummary errors={{ page: loadError }} /><Button onClick={() => navigate(`/plans/${publicId}`)}>Back to your overview</Button></section> : <LoadingState label="Loading the current question" />;
  return (
    <RefinementPage
      estimate={estimate}
      requestedCode={requestedCode}
      online={online}
      announce={announce}
      onBack={() => navigate(`/plans/${publicId}`)}
      onEstimateChange={setEstimate}
      submit={(inputVersion, inputCode, value, key) => refinePlan(publicId, inputVersion, inputCode, value, key)}
    />
  );
}
